import { useCallback, useEffect, useMemo } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { isNumber, map } from 'lodash-es'
import {
  ChevronLeft,
  ChevronRight,
  FilterIcon,
  Grid3x3,
  List,
  SortAscIcon,
  SortDescIcon,
  XIcon,
} from 'lucide-react'

import { dayjs } from '@/lib/dayjs.ts'
import { isDateField } from '@/lib/json-schema-utils.ts'
import { cn } from '@/lib/utils.ts'
import { PAGINATION_DOTS, usePagination } from '@/hooks/use-pagination.ts'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx'
import { DateFormat } from '@/components/date-format.tsx'
import { Empty } from '@/components/empty.tsx'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { useGetAttributeGroup } from '@/features/attribute-groups/hooks/use-get-attribute-group.ts'
import { useFindAttributes } from '@/features/attributes/hooks/use-find-attributes.ts'
import { documentStatusLabels } from '@/features/documents/constants.ts'
import { DocumentAttributeRender } from '@/features/documents/document-attribute-render.tsx'
import { DocumentsGrid } from '@/features/documents/documents-grid.tsx'
import { DocumentsList } from '@/features/documents/documents-list.tsx'
import { useSearchDocuments } from '@/features/documents/hooks/use-search-documents.ts'
import { SearchInput } from '@/routes/_authenticated/documents/browse/-components/search-input.tsx'
import { filtersValuesSchema } from '@/routes/_authenticated/documents/browse/-schemas.ts'

export const Route = createFileRoute('/_authenticated/documents/browse/')({
  component: DocumentsBrowseRoute,
  validateSearch: filtersValuesSchema,
  onError() {
    throw redirect({
      to: '/documents/browse',
    })
  },
})

function DocumentsBrowseRoute() {
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()

  const attributesQuery = useFindAttributes()
  const attributes = attributesQuery?.data?.data

  const paginationCtrl = usePagination({
    currentPage: searchParams.page ?? 1,
    pageSize: 40,
  })

  // View mode: 'list' or 'grid', default to 'list'
  const view = searchParams.view ?? 'list'

  // Determine the status filter for the API call
  const statusFilter = searchParams.statusFilter ?? 'both' // Default to 'both'
  const statusQuery = useMemo(() => {
    switch (statusFilter) {
      case 'ready':
        return { in: ['ready' as const] }
      case 'in_review':
        return { in: ['in_review' as const] }
      case 'both':
        return { in: ['ready' as const, 'in_review' as const] }
      default:
        return { in: ['ready' as const] }
    }
  }, [statusFilter])

  const documentsQuery = useSearchDocuments({
    status: statusQuery,
    attributes: searchParams.attributes,
    attributeGroupId: searchParams.attributeGroupId,
    searchTerm: searchParams.searchTerm,
    orderBy: searchParams.orderBy,
    offset: paginationCtrl.skip,
    limit: paginationCtrl.limit,
  })
  const documentsQueryData = documentsQuery.data?.data
  const documents = documentsQueryData?.data
  const documentsTotal = documentsQueryData?.total
  useEffect(() => {
    if (isNumber(documentsTotal)) {
      paginationCtrl.setTotal(documentsTotal)
    }
  }, [documentsTotal, paginationCtrl])

  useEffect(() => {
    if (paginationCtrl.currentPage !== searchParams.page) {
      navigate({
        search: (s) => ({ ...s, page: paginationCtrl.currentPage }),
      })
    }
  }, [navigate, paginationCtrl.currentPage, searchParams.page])

  const handleTextFilterChange = (searchTerm: string) => {
    navigate({ search: { ...searchParams, searchTerm } })
  }
  const handleAttributeFilterChange = ({
    attributeId,
    value,
  }: {
    attributeId: string
    value: unknown
  }) => {
    navigate({
      search: {
        ...searchParams,
        attributes: {
          ...searchParams.attributes,
          [attributeId]: value === null ? undefined : value,
        },
      },
    })
  }

  const handleAttributeGroupFilterChange = (args?: {
    attributeGroupId: string
  }) => {
    const { attributeGroupId } = args ?? {}

    navigate({
      search: {
        ...searchParams,
        attributeGroupId,
      },
    })
  }

  const attributeFilters = useMemo(() => {
    return map(searchParams.attributes, (value, attributeId) => ({
      attributeId,
      value,
      attribute: attributes?.find((a) => a.id === attributeId),
    }))
  }, [attributes, searchParams.attributes])

  const dateAttributes = useMemo(
    () =>
      attributes?.filter(
        (attribute) =>
          attribute.json_schema?.type === 'string' &&
          ['date', 'datetime'].includes(attribute.json_schema?.format ?? '')
      ),
    [attributes]
  )

  const dateOptions = useMemo(
    () => [
      { name: 'Data caricamento', key: 'created_at' },
      ...(dateAttributes?.map((a) => ({ name: a.name, key: a.id })) ?? []),
    ],
    [dateAttributes]
  )

  const orderByEntries = useMemo(() => {
    if (!searchParams.orderBy) return

    const key = Object.keys(searchParams.orderBy)[0]

    if (key) {
      return [key, searchParams?.orderBy[key]]
    }
  }, [searchParams.orderBy])

  const orderByAttribute = useMemo(() => {
    if (!orderByEntries?.[0]) return

    return dateOptions.find((option) => option.key === orderByEntries[0])
  }, [dateOptions, orderByEntries])

  const handleOrderByClick = (attributeId: string | null) => {
    if (!attributeId) {
      return navigate({
        search: {
          ...searchParams,
          orderBy: undefined,
        },
      })
    }

    let direction = 'desc'
    if (attributeId === orderByEntries?.[0]) {
      direction = orderByEntries?.[1] === 'desc' ? 'asc' : 'desc'
    }

    return navigate({
      search: {
        ...searchParams,
        orderBy: { [attributeId]: direction as 'asc' | 'desc' },
      },
    })
  }

  const getAttributeGroupQuery = useGetAttributeGroup(
    { attributeGroupId: searchParams.attributeGroupId! },
    { enabled: !!searchParams.attributeGroupId }
  )
  const selectedAttributeGroup = getAttributeGroupQuery.data?.success
    ? getAttributeGroupQuery.data?.data
    : null

  const handleStatusFilterChange = useCallback(
    (newStatusFilter: typeof statusFilter) => {
      navigate({
        search: {
          ...searchParams,
          statusFilter: newStatusFilter,
          page: 1, // Reset to first page when changing filter
        },
      })
    },
    [navigate, searchParams]
  )

  const handleViewChange = useCallback(
    (newView: 'list' | 'grid') => {
      navigate({
        search: {
          ...searchParams,
          view: newView,
        },
      })
    },
    [navigate, searchParams]
  )

  // Status filter options with Italian labels - memoized for performance
  const statusOptions = useMemo(
    () =>
      [
        { value: 'ready', label: documentStatusLabels.ready },
        { value: 'in_review', label: documentStatusLabels.in_review },
        { value: 'both', label: 'Tutti' },
      ] as const,
    [documentStatusLabels.ready, documentStatusLabels.in_review]
  )

  const selectedStatusOption = useMemo(
    () => statusOptions.find((option) => option.value === statusFilter),
    [statusOptions, statusFilter]
  )

  // Memoized button text content for better performance
  const statusButtonText = useMemo(
    () => ({
      desktop:
        statusFilter !== 'both'
          ? `Stato: ${selectedStatusOption?.label}`
          : 'Stato',
      mobile: selectedStatusOption?.label ?? 'Stato',
    }),
    [statusFilter, selectedStatusOption?.label]
  )

  return (
    <ScrollArea className="h-full">
      <div className="p-4">
        <Breadcrumbs items={[{ name: 'Documenti' }]} />
        <PageTitle>Documenti</PageTitle>

        <div className="mb-4 flex flex-col gap-3 pr-4 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex-1">
            <SearchInput
              onTextFilterChange={handleTextFilterChange}
              onAttributeFilterChange={handleAttributeFilterChange}
              onAttributeGroupFilterChange={handleAttributeGroupFilterChange}
            />
          </div>

          <div className="flex gap-2 sm:shrink-0">
            {/* Status Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="min-w-0 shrink-0 text-sm">
                  <FilterIcon className="size-4 shrink-0 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {statusButtonText.desktop}
                  </span>
                  <span className="sm:hidden">{statusButtonText.mobile}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]">
                {statusOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => handleStatusFilterChange(option.value)}
                    className="flex items-center justify-between"
                  >
                    <span>{option.label}</span>
                    {statusFilter === option.value && (
                      <span className="text-primary ml-2">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort Dropdown */}
            <DropdownMenu>
              <div className="flex">
                <DropdownMenuTrigger asChild>
                  <Button
                    className={cn(
                      orderByEntries?.[0] && 'rounded-r-none',
                      'min-w-0 text-sm'
                    )}
                    variant="outline"
                  >
                    {orderByEntries?.[1] === 'asc' && (
                      <SortAscIcon className="mr-1 size-4 shrink-0 sm:mr-2" />
                    )}
                    {orderByEntries?.[1] === 'desc' && (
                      <SortDescIcon className="mr-1 size-4 shrink-0 sm:mr-2" />
                    )}
                    <span className="hidden truncate sm:inline">
                      {orderByAttribute?.name ?? 'Ordina per'}
                    </span>
                    <span className="truncate sm:hidden">
                      {orderByAttribute?.name ?? 'Ordina'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                {orderByEntries?.[0] && (
                  <Button
                    className="rounded-l-none border-l-0"
                    variant="outline"
                    size="icon"
                    onClick={() => handleOrderByClick(null)}
                  >
                    <XIcon className="size-4" />
                  </Button>
                )}
              </div>
              <DropdownMenuContent align="start" className="min-w-[180px]">
                {dateOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.key}
                    onClick={() => handleOrderByClick(option.key)}
                    className="flex items-center justify-between py-2.5"
                  >
                    <span>{option.name}</span>
                    {orderByEntries?.[0] === option.key ? (
                      <>
                        {orderByEntries[1] === 'asc' ? (
                          <SortAscIcon className="ml-2 size-4" />
                        ) : (
                          <SortDescIcon className="ml-2 size-4" />
                        )}
                      </>
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Toggle */}
            <TooltipProvider delayDuration={0}>
              <ToggleGroup
                type="single"
                value={view}
                onValueChange={(value) => {
                  if (value) handleViewChange(value as 'list' | 'grid')
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem
                      value="list"
                      aria-label="Toggle list"
                      className={cn(
                        view === 'list' &&
                          'bg-accent text-accent-foreground'
                      )}
                    >
                      <List className="h-4 w-4" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Layout lista</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem
                      value="grid"
                      aria-label="Toggle grid"
                      className={cn(
                        view === 'grid' &&
                          'bg-accent text-accent-foreground'
                      )}
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Layout griglia</p>
                  </TooltipContent>
                </Tooltip>
              </ToggleGroup>
            </TooltipProvider>
          </div>
        </div>

        {(searchParams.searchTerm ||
          searchParams.attributeGroupId ||
          attributeFilters?.length > 0 ||
          statusFilter !== 'both') && (
          <div className="mb-3 flex flex-wrap gap-2 sm:mb-2 sm:gap-x-2">
            {searchParams.searchTerm && (
              <Badge
                className="mb-2 line-clamp-1 flex px-4 py-2"
                variant="default"
              >
                Ricerca per testo: {searchParams.searchTerm}{' '}
                <XIcon
                  className="ml-2 size-4 min-h-4 min-w-4 cursor-pointer"
                  onClick={() => handleTextFilterChange('')}
                />
              </Badge>
            )}
            {statusFilter !== 'both' && (
              <Badge
                className="mb-2 line-clamp-1 flex px-4 py-2"
                variant="default"
              >
                Stato: {selectedStatusOption?.label}{' '}
                <XIcon
                  className="ml-2 size-4 min-h-4 min-w-4 cursor-pointer"
                  onClick={() => handleStatusFilterChange('both')}
                />
              </Badge>
            )}
            {searchParams.attributeGroupId && (
              <Badge
                className="mb-2 line-clamp-1 flex px-4 py-2"
                variant="default"
              >
                Gruppo Attributi: {selectedAttributeGroup?.name}{' '}
                <XIcon
                  className="ml-2 size-4 min-h-4 min-w-4 cursor-pointer"
                  onClick={() => handleAttributeGroupFilterChange()}
                />
              </Badge>
            )}
            {attributesQuery.isSuccess &&
              attributeFilters.map(({ attribute, value }) => (
                <Badge
                  key={attribute!.id}
                  className="mb-2 px-4 py-2"
                  variant="default"
                >
                  {attribute!.name}:{' '}
                  {isDateField(attribute!.json_schema) ? (
                    <>
                      <DateFormat date={value.from} />
                      {!dayjs(value.from).isSame(value.to) ? (
                        <>
                          &nbsp;- <DateFormat date={value.to} />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <DocumentAttributeRender
                      attributeJsonSchema={attribute!.json_schema}
                      value={value}
                    />
                  )}
                  <XIcon
                    className="ml-2 size-4 min-h-4 min-w-4 cursor-pointer"
                    onClick={() =>
                      handleAttributeFilterChange({
                        attributeId: attribute!.id,
                        value: null,
                      })
                    }
                  />
                </Badge>
              ))}
          </div>
        )}

        <QuerySpinner wrapperClassName="py-10" query={documentsQuery}>
          <ScrollArea className="pr-4">
            {view === 'grid' ? (
              <DocumentsGrid documents={documents} statusVisible={true} />
            ) : (
              <DocumentsList documents={documents} statusVisible={true} />
            )}

            {paginationCtrl.paginationRange.length > 1 && (
              <Pagination className="py-5">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationLink
                      className="w-auto cursor-pointer gap-1 px-4 pl-2.5"
                      {...paginationCtrl.getPrevButtonProps()}
                    >
                      <ChevronLeft className="size-4" />
                      <span>Indietro</span>
                    </PaginationLink>
                  </PaginationItem>
                  {paginationCtrl.paginationRange.map((pageNum, index) => (
                    <PaginationItem key={`${pageNum} + ${index}`}>
                      {pageNum === PAGINATION_DOTS ? (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationLink
                          className="cursor-pointer"
                          isActive={pageNum === searchParams.page}
                          {...(pageNum !== PAGINATION_DOTS &&
                            paginationCtrl.getGoToPageButtonProps(
                              pageNum as number
                            ))}
                        >
                          {pageNum}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationLink
                      className="w-auto cursor-pointer gap-1 px-4 pr-2.5"
                      {...paginationCtrl.getNextButtonProps()}
                    >
                      <span>Avanti</span>
                      <ChevronRight className="size-4" />
                    </PaginationLink>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </ScrollArea>

          {documentsQuery.isSuccess && !documents?.length && <Empty />}
        </QuerySpinner>

        {/*{documentsQuery.isPending && (*/}
        {/*  <div className="py-10">*/}
        {/*    <LoadingSpinner />*/}
        {/*  </div>*/}
        {/*)}*/}

        {/*{documentsQuery.isSuccess && (*/}
        {/*  <ScrollArea className="pr-4">*/}
        {/*    <DocumentsList documents={documents} />*/}

        {/*    {paginationCtrl.paginationRange.length > 1 && (*/}
        {/*      <Pagination className="py-5">*/}
        {/*        <PaginationContent>*/}
        {/*          <PaginationItem>*/}
        {/*            <PaginationPrevious*/}
        {/*              className="cursor-pointer"*/}
        {/*              {...paginationCtrl.getPrevButtonProps()}*/}
        {/*            />*/}
        {/*          </PaginationItem>*/}
        {/*          {paginationCtrl.paginationRange.map((pageNum, index) => (*/}
        {/*            <PaginationItem key={`${pageNum} + ${index}`}>*/}
        {/*              {pageNum === PAGINATION_DOTS ? (*/}
        {/*                <PaginationItem>*/}
        {/*                  <PaginationEllipsis />*/}
        {/*                </PaginationItem>*/}
        {/*              ) : (*/}
        {/*                <PaginationLink*/}
        {/*                  className="cursor-pointer"*/}
        {/*                  isActive={pageNum === searchParams.page}*/}
        {/*                  {...(pageNum !== PAGINATION_DOTS &&*/}
        {/*                    paginationCtrl.getGoToPageButtonProps(*/}
        {/*                      pageNum as number*/}
        {/*                    ))}*/}
        {/*                >*/}
        {/*                  {pageNum}*/}
        {/*                </PaginationLink>*/}
        {/*              )}*/}
        {/*            </PaginationItem>*/}
        {/*          ))}*/}
        {/*          <PaginationItem>*/}
        {/*            <PaginationNext*/}
        {/*              className="cursor-pointer"*/}
        {/*              {...paginationCtrl.getNextButtonProps()}*/}
        {/*            />*/}
        {/*          </PaginationItem>*/}
        {/*        </PaginationContent>*/}
        {/*      </Pagination>*/}
        {/*    )}*/}
        {/*  </ScrollArea>*/}
        {/*)}*/}

        {/*{documentsQuery.isSuccess && !documents?.length && <Empty />}*/}
      </div>
    </ScrollArea>
  )
}
